import {
    Absence,
    AbsenceType,
    ActivityLogItem,
    ActivityTodoItem,
    ActivityType,
    Address,
    Asset,
    AssetStatus,
    AuditLog,
    BankAccount,
    Cct,
    Client,
    ClientInfo,
    ClientProfile,
    Contract,
    ContractSignatureStatus,
    ContractState,
    DateScheduleType,
    Document,
    DocumentType,
    EarningType,
    EducationLevel,
    FeeType,
    Gender,
    IjmGroup,
    Invitation,
    InvitationMode,
    Invoice,
    KycStatus,
    LaaActivityType,
    LppFund,
    LppPlan,
    MaritalStatus,
    Mission,
    MissionCancellationRequest,
    MissionCount,
    MissionDeduction,
    MissionFee,
    MissionInfo,
    MissionOvertimeStatus,
    MissionVacationSatus,
    PageInfo,
    PayrollBreakdown,
    PayrollBreakdownStatus,
    PayrollBreakdownSummary,
    PayrollSourceType,
    Repetition,
    RepetitionType,
    SalaryDeductionType,
    SalaryDetails,
    SalaryForecast,
    SalaryType,
    ScheduleTimeType,
    ScheduleType,
    SimpleSigningState,
    TerminatedByType,
    TerminateReason,
    TimeMode,
    TimeSheet,
    TimeSheetAcceptedBy,
    TimeSheetState,
    TrackedDurationBreakdownType,
    Unit,
    User,
    WorkerInfo,
    WorkerProfile,
    WorkingPermitStatus,
    WorkingTimeDurationBreakdownItemType,
    ClientUser,
    DateScheduleInput,
    CctDetailsFieldValue,
    CctDetailsField,
    UserInfo,
    WorkingPermit,
    MissionSalary,
    Salary,
} from "../__generated__/graphql";
import { formatDateForGraphql, MissionWithIjm } from "../utils";
import {
    addDays,
    addMonths,
    differenceInCalendarDays,
    endOfMonth,
    startOfMonth,
    startOfWeek,
} from "date-fns";
import { cloneDeep, set } from "lodash";
import { Buildable } from "ts-essentials";
import { v4 as uuidv4 } from "uuid";
import { GET_IJM_CHOICES } from "../graphql/client";

function combineProps<T>(base: Partial<T>, props: Buildable<T>): NonNullable<T> {
    const result = cloneDeep(base || {});
    if (props) {
        for (const [key, value] of Object.entries(props)) {
            if (value !== undefined) {
                set(result, key, value);
            }
        }
    }
    return result as NonNullable<T>;
}

export const buildUuid = () => uuidv4().toString();

let CURRENT_ID = 0;

export const buildId = (prefix: string) => {
    CURRENT_ID += 1;
    return `${prefix}-${CURRENT_ID}`;
};

export const buildEmail = () => {
    CURRENT_ID += 1;
    return `user-${CURRENT_ID}@test.com`;
};

export const buildName = (prefix: string) => {
    CURRENT_ID += 1;
    return `${prefix} ${CURRENT_ID}`;
};

export const buildBankAccount = (props?: Partial<BankAccount>) => {
    const base = {
        __typename: "BankAccount",
        id: buildUuid(),
        iban: "CH9300762011623852957",
        name: "PostFinance",
    };
    return combineProps(base, props) as BankAccount;
};

export const buildClientInfo = (props?: Partial<ClientInfo>) => {
    const base = {
        id: buildUuid(),
        uid: buildUuid(),
        __typename: "ClientInfo",
        name: "Trikounix SA",
        profileName: "Kevin Kliant",
        email: "kevin.kliant@trikounix.ch",
        avatarUrl:
            "https://storage.googleapis.com/lift-public-assets/dev-storybook/client-avatar.png",
        logoUrl: "https://storage.googleapis.com/lift-public-assets/dev-storybook/client-logo.jpg",
        address: buildAddress({}),
        hoursPerWeekFullTime: 42,
        phoneNumber: "+41799999901",
        verified: true,
        deleted: false,
        isPrivateClient: false,
        client: {
            name: "Trikounix SA",
        },
    };
    return combineProps(base, props) as ClientInfo;
};

export const buildClient = (props?: Partial<Client>) => {
    const base = {
        __typename: "Client",
        id: buildUuid(),
        address: buildAddress(),
        hoursPerWeekFullTime: 42,
        logoUrl: getAvatarUrl2(),
        name: "Trikounix SA",
        uid: "CH-550-1198767-4",
        verified: true,
        ijmGroup: null,
        canChangeIjmGroup: true,
    };
    return combineProps(base, props) as Client;
};

export const buildClientProfile = (props?: Partial<ClientProfile>) => {
    const base = {
        __typename: "ClientProfile",
        id: buildUuid(),
        client: buildClient(),
        businessEmail: "client@test.com",
        isPrivateClient: false,
    };
    return combineProps(base, props) as ClientProfile;
};

export const buildWorkerProfile = (props?: Partial<WorkerProfile>) => {
    const base = {
        __typename: "WorkerProfile",
        id: buildUuid(),
        avsNumber: "756.1234.5678.97",
        bankAccount: buildBankAccount(),
        phoneNumber: "+41799999901",
        maritalStatus: MaritalStatus.Single,
        partner: null,
        educationLevel: EducationLevel.Baccalaureate,
        yearsOfExperience: 5,
        maritalStatusFrom: null,
        hasOtherActivity: false,
        hasChildren: false,
        hasFiscalResidence: false,
        lppRegistrations: [],
        documents: [],
        relationshipCount: 0,
    };
    return combineProps(base, props) as WorkerProfile;
};

export const buildMissionCount = (props?: Partial<MissionCount>) => {
    const base = {
        __typename: "MissionCount",
        accepted: 0,
        cancelled: 0,
        draft: 0,
        ended: 0,
        pendingAcceptation: 0,
        terminated: 0,
        total: 0,
    };
    return combineProps(base, props) as MissionCount;
};

export const buildWorkerInfo = (props?: Partial<WorkerInfo>) => {
    const base = {
        __typename: "WorkerInfo",
        id: buildUuid(),
        name: "Wilma Worqueur",
        email: buildEmail(),
        avatarUrl:
            "https://storage.googleapis.com/lift-public-assets/dev-storybook/worker-avatar-3.gif",
        address: null,
        phoneNumber: "+41799999901",
        birthdate: "1990-01-01",
        gender: Gender.Male,
        deleted: false,
        educationLevel: EducationLevel.Baccalaureate,
        avsNumber: "756.1234.5678.90",
        yearsOfExperience: 5,
        missions: [],
        missionCount: buildMissionCount(),
        documents: [],
        workPermitStatus: WorkingPermitStatus.Valid,
    };
    return combineProps(base, props) as WorkerInfo;
};

export const buildMissionInfo = (props?: Partial<MissionInfo>) => {
    const base = {
        __typename: "MissionInfo",
        id: buildUuid(),
        name: "Mission de test",
        clientInfo: buildClientInfo(),
        workerInfo: buildWorkerInfo(),
        earningType: EarningType.Monthly,
        activityType: buildActivityType(),
        laaActivityType: LaaActivityType.Office,
        payrollSource: PayrollSourceType.Lift,
        workingTimeClassicSpec: EARNING_TYPE_SAMPLES[props?.["earningType"] || EarningType.Monthly],
    };
    return combineProps(base, props) as MissionInfo;
};

export const buildMissionFee = (props?: Partial<MissionFee>) => {
    const base = {
        id: buildUuid(),
        externalId: buildId("ext"),
        type: FeeType.Food,
        amount: 2000,
        invoiceAmount: 2100,
        repetition: RepetitionType.WorkedDay,
        otherTitle: null,
    };
    return combineProps(base, props) as MissionFee;
};

export const buildMissionDeduction = (props?: Partial<MissionDeduction>) => {
    const base = {
        id: buildUuid(),
        externalId: buildId("ext"),
        description: "Petit croissant du matin",
        amount: 2000,
        repetition: RepetitionType.WorkedDay,
    };
    return combineProps(base, props) as MissionDeduction;
};
export const buildLppFund = (props?: Partial<LppFund>) => {
    const base = {
        id: buildId("lpp-fund"),
        name: buildName("Sample fund"),
        needRegistration: false,
    };
    return combineProps(base, props) as LppFund;
};

export const buildLppPlan = (props?: Partial<LppPlan>) => {
    const base = {
        assetUrl: "https://www.test.io",
        fromYearlySalary: 10000,
        fund: buildLppFund(),
        id: buildId("lpp-plan"),
        name: buildName("Sample plan"),
    };
    return combineProps(base, props) as LppPlan;
};

export const EARNING_TYPE_SAMPLES = {
    [EarningType.Monthly]: {
        repetition: Repetition.Week,
        unit: Unit.Hours,
        unitCount: 42,
        dailyHours: null,
    },
    [EarningType.Daily]: {
        repetition: Repetition.Month,
        unit: Unit.Days,
        unitCount: 10,
        dailyHours: 42,
    },
    [EarningType.Hourly]: {
        repetition: Repetition.Week2,
        unit: Unit.Hours,
        unitCount: 5,
        dailyHours: null,
    },
    [EarningType.Package]: {
        repetition: Repetition.None,
        unit: Unit.Hours,
        unitCount: 30,
        dailyHours: 42,
    },
};
export const buildContract = (props?: Partial<Contract>): Contract => {
    const base = {
        __typename: "Contract",
        id: buildUuid(),
        state: ContractState.Accepted,
        signatureStatus: ContractSignatureStatus.Success,
        signingState: SimpleSigningState.Signed,
        jobTitle: "Pilote de Drone pour la Cinématographie",
        jobDescription:
            "En tant que pilote de drone pour la cinématographie, vous capturerez des prises de vue aériennes spectaculaires pour des films, des publicités et des événements spéciaux, ajoutant une dimension visuelle unique aux projets.",
        cctEditionId: "1",
        cctContractId: "815001",
        startDate: formatDateForGraphql(addDays(new Date(), 0)),
        endDate: formatDateForGraphql(addDays(new Date(), 20)),
        effectiveEndDate: formatDateForGraphql(addDays(new Date(), 20)),
        terminatedDate: null,
        terminatedBy: null,
        terminationOtherReason: null,
        workingTimeScheduleSpec: null,
        workingTimeSpecificSpec: null,
        workingTimeClassicSpec: EARNING_TYPE_SAMPLES[props?.["earningType"] || EarningType.Monthly],
        clientContractAsset: null,
        workerContractAsset: null,
        updatedAt: formatDateForGraphql(addDays(new Date(), -1)),
        createdAt: formatDateForGraphql(addDays(new Date(), -1)),
        hoursPerWeekFullTime: 42,
        cctDetails: { tempdataCallValues: [], readableValues: [], fullValues: [] },
        earningType: EarningType.Monthly,
        vacationDays: 16,
        has13thSalary: false,
        invoiceSalary: 600000,
        netSalary: 450000,
        grossSalary: 500000,
        baseSalary: 550000,
        useClientWorkingAddress: false,
        workingPercentage: 100,
        clientAcceptedAt: null,
        workerAcceptedAt: null,
        terminationReason: null,
        terminationDate: null,
        clientContractSignedAt: null,
        workerContractSignedAt: null,
        clientContractSealedAt: null,
        workerContractSealedAt: null,
        contractEffectiveAsset: {
            id: "test-asset-id",
            status: AssetStatus.Ready,
            url: "https://www.test.io",
        },
        fees: [buildMissionFee()],
        deductions: [buildMissionDeduction()],
        lppPlan: buildLppPlan(),
        lseMargin: 20,
        publicHolidaysCompensation: 31,
        thirteenMonthCompensation: 550,
        vacationCompensation: 130,
        clientDeductions: [buildSalaryDeduction()],
        workerDeductions: [buildSalaryDeduction()],
        workingAddress: buildAddress(),
        ijmInfo: {
            ijmGroup: buildIjmGroup(),
            payFirstDays: false,
            payRemainingPercents: 0,
        },
    };
    return combineProps(base, props) as Contract;
};

export const buildActivityType = (props?: Partial<ActivityType>) => {
    const base = {
        __typename: "ActivityType",
        id: buildUuid(),
        title: "Parachutiste",
        ofsSalariumGroupId: buildId("ofs-salarium-group"),
        ofsSalariumId: buildId("ofs-salarium"),
    };
    return combineProps(base, props) as ActivityType;
};

export const buildMissionDocument = (props?: Partial<Document>) => {
    const base = {
        __typename: "Document",
        type: DocumentType.Other,
        id: buildUuid(),
        title: "Sample document",
        documentUrl: "https://www.test.io",
        asset: buildAsset(),
    };
    return combineProps(base, props) as Document;
};
/**
 * Build a mission object compatible with the GraphQL Mission type
 * @param props change properties key: value where key is lodash "set" compatible (https://lodash.com/docs/4.17.15#set)
 */
export const buildMission = (props?: Partial<Mission>) => {
    const base = {
        __typename: "Mission",
        id: buildUuid(),
        name: buildName("Mission"),
        updatedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        educationLevel: null,
        activityType: buildActivityType(),
        currentContract: buildContract(),
        documents: [buildMissionDocument()],
        clientInfo: buildClientInfo(),
        workerInfo: buildWorkerInfo(),
        laaActivityType: LaaActivityType.Office,
        payrollSource: PayrollSourceType.Lift,
    };
    return combineProps(base, props) as Mission;
};

export const buildCreateMission = (props?: Partial<MissionWithIjm>) => {
    const base = {
        __typename: "Mission",
        id: buildUuid(),
        name: buildName("Mission"),
        updatedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        educationLevel: null,
        activityType: buildActivityType(),
        currentContract: buildContract(),
        documents: [buildMissionDocument()],
        clientInfo: buildClientInfo(),
        workerInfo: buildWorkerInfo(),
        laaActivityType: LaaActivityType.Office,
        payrollSource: PayrollSourceType.Lift,
        ijmSelection: {
            ijmGroupId: "ijm-group-1",
            ijmGroupName: "Group plan 1",
            ijmPayFirstDays: true,
            ijmPayRemainingPercents: 10,
        },
    };
    return combineProps(base, props) as MissionWithIjm;
};

export const buildActivityTodo = (props?: Partial<ActivityTodoItem>) => {
    const base = {
        __typename: "ActivityTodoItem",
        id: buildUuid(),
        createdAt: new Date().toISOString(),
        activityName: "VerifyEmail",
        activityKey: null,
        title: "Sample activity todo",
        text: "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
        priority: 3,
        badgeUrl: "https://cdn-icons-png.flaticon.com/128/9285/9285393.png",
        sourceName: "The source name",
    };
    return combineProps(base, props);
};

export const buildActivityLog = (props?: Partial<ActivityLogItem>) => {
    const base = {
        __typename: "ActivityLogItem",
        id: buildUuid(),
        createdAt: new Date().toISOString(),
        activityName: "EmailVerified",
        activityKey: null,
        title: "Sample activity log",
        text: "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
        priority: 2,
        badgeUrl:
            "https://cdn.iconscout.com/icon/premium/png-256-thumb/planet-2147391-1805474.png?f=webp",
        sourceName: "The source name",
    };
    return combineProps(base, props);
};

export const buildAsset = (props?: Partial<Asset>) => {
    const base = {
        __typename: "Asset",
        id: buildUuid(),
        status: AssetStatus.Ready,
        url: "https://storage.googleapis.com/lift-public-assets/sample-pdf-a4-size-65kB.pdf",
        error: null,
    };
    return combineProps(base, props) as Asset;
};

export const buildInvoice = (props?: Partial<Invoice>) => {
    const base = {
        __typename: "Invoice",
        id: buildUuid(),
        fromDate: new Date().toISOString().substring(0, 10),
        toDate: new Date().toISOString().substring(0, 10),
        paidAt: new Date().toISOString(),
        sentAt: new Date().toISOString(),
        invoiceNumber: "invoice-token-number",
        totalAmount: 12345,
        missionInfo: buildMissionInfo(),
        invoiceAsset: buildAsset(),
    };
    return combineProps(base, props);
};

export const getAvatarUrl = () => {
    return "https://storage.googleapis.com/lift-public-assets/dev-storybook/worker-avatar.jpg";
};

export const getAvatarUrl2 = () => {
    return "https://storage.googleapis.com/lift-public-assets/dev-storybook/worker-avatar-2.jpg";
};

export const buildTrackingDate = (
    props?: Partial<DateScheduleType> | undefined,
): DateScheduleType => {
    const base = {
        date: formatDateForGraphql(addDays(new Date(), 0)),
        daySchedule: {
            __typename: "DayScheduleType",
            timeMode: TimeMode.Fixed,
            freeDurations: [],
            fixedTimes: [
                {
                    startTime: 60 * 8,
                    endTime: 60 * 12,
                    type: ScheduleTimeType.Work,
                },
                {
                    startTime: 60 * 13,
                    endTime: 60 * 17,
                    type: ScheduleTimeType.Work,
                },
            ],
        },
        times: [
            {
                startTime: 60 * 8,
                endTime: 60 * 12,
                type: ScheduleTimeType.Work,
            },
            {
                startTime: 60 * 13,
                endTime: 60 * 17,
                type: ScheduleTimeType.Vacation,
            },
        ],
    };
    return combineProps(base, props) as DateScheduleType;
};

export const buildTrackingDateAlternative = (
    props?: Partial<DateScheduleType> | undefined,
): DateScheduleType => {
    const base = {
        date: formatDateForGraphql(addDays(new Date(), -1)),
        daySchedule: {
            timeMode: TimeMode.Fixed,
            fixedTimes: [
                {
                    startTime: 60 * 8,
                    endTime: 60 * 12,
                    type: ScheduleTimeType.Trainee,
                },
                {
                    startTime: 60 * 13,
                    endTime: 60 * 17,
                    type: ScheduleTimeType.Trainee,
                },
            ],
        },
        times: [
            {
                startTime: 60 * 8,
                endTime: 60 * 12,
                type: ScheduleTimeType.Trainee,
            },
            {
                startTime: 60 * 13,
                endTime: 60 * 17,
                type: ScheduleTimeType.Trainee,
            },
        ],
    };
    return combineProps(base, props) as DateScheduleType;
};

export const buildTimesheet = (props?: Partial<TimeSheet>) => {
    const fromDate = props?.fromDate || formatDateForGraphql(addDays(new Date(), -1));
    const toDate = props?.toDate || formatDateForGraphql(addDays(new Date(), 6));
    const base = {
        __typename: "TimeSheet",
        id: buildUuid(),
        status: TimeSheetState.Accepted,
        fromDate,
        toDate,
        acceptedAt: "2021-02-10",
        autoAcceptAt: "2021-02-10",
        submittedAt: "2021-02-08",
        rejectedAt: "2021-02-08",
        acceptedBy: TimeSheetAcceptedBy.Client,
        trackedDurationBreakdown: {
            __typename: "TrackedDurationBreakdownType",
            items: [
                {
                    hourRate: 100,
                    type: WorkingTimeDurationBreakdownItemType.Day,
                    totalDurationMinutes: 120,
                },
                {
                    hourRate: 125,
                    type: WorkingTimeDurationBreakdownItemType.Night,
                    totalDurationMinutes: 60,
                },
                {
                    hourRate: 150,
                    type: WorkingTimeDurationBreakdownItemType.Overtime,
                    totalDurationMinutes: 85,
                },
                {
                    hourRate: 100,
                    type: WorkingTimeDurationBreakdownItemType.Sickness,
                    totalDurationMinutes: 60 * 4,
                },
                {
                    hourRate: 100,
                    type: WorkingTimeDurationBreakdownItemType.Trainee,
                    totalDurationMinutes: 60 * 8,
                },
            ],
        },
        isExactTimeSheet: true,
        totalTrackedDurationMinutes: 270,
        trackingDates: buildTrackingDates(fromDate, toDate),
        missionInfo: buildMissionInfo(),
        contractWeeklyDurationMinutes: 42 * 60,
    };
    return combineProps(base, props) as TimeSheet;
};

export const buildTrackingDates = (fromDate: string, toDate: string): DateScheduleType[] => {
    // create a list of DateScheduleType for each date between fromDate and toDate
    const start = new Date(fromDate);
    const end = new Date(toDate);
    const daysCount = differenceInCalendarDays(end, start) + 1;

    return Array.from({ length: daysCount }, (_, i) => {
        const date = addDays(start, i);
        return buildTrackingDate({ date: date.toISOString().split("T")[0] });
    });
};

export const buildAddress = (props?: Partial<Address>): Address => {
    CURRENT_ID += 1;
    const base = {
        __typename: "Address",
        formatted: `Rue de la Gare ${CURRENT_ID}, 1000 Lausanne`,
        latitude: 46.519653,
        longitude: 6.632273,
        route: "Rue de la Gare",
        streetNumber: `${CURRENT_ID}`,
        locality: {
            __typename: "Locality",
            name: "Lausanne",
            postalCode: "1000",
            state: {
                __typename: "State",
                code: "VD",
                name: "Vaud",
                country: {
                    __typename: "Country",
                    code: "CH",
                    name: "Switzerland",
                },
            },
        },
    };
    return combineProps(base, props) as Address;
};

export const buildUserInfo = (props?: Partial<UserInfo>): UserInfo => {
    const base = {
        __typename: "UserInfo",
        id: buildUuid(),
        firstName: "John",
        lastName: "Doe",
        avatarUrl: getAvatarUrl(),
    };
    return combineProps(base, props) as UserInfo;
};

export const buildUser = (props?: Partial<User>): User => {
    const base = {
        __typename: "User",
        id: buildUuid(),
        firstName: "John",
        lastName: "Doe",
        email: "john@doe.com",
        birthdate: "1990-01-01",
        phoneNumber: "+41799999901",
        gender: Gender.Male,
        kycStatus: {
            status: KycStatus.Accepted,
            veriffUrl: "https://veriff.com",
        },
        address: buildAddress(),
        avatarUrl: getAvatarUrl(),
        workPermit: WorkingPermit.PermitC,
        nationality: "FR",
    };
    return combineProps(base, props) as User;
};

export const buildBreakdown = (props?: Partial<TrackedDurationBreakdownType>) => {
    const base = {
        __typename: "TrackedDurationBreakdownType",
        items: [
            {
                hourRate: 100,
                type: WorkingTimeDurationBreakdownItemType.Day,
                totalDurationMinutes: 120,
            },
        ],
    };
    return combineProps(base, props) as TrackedDurationBreakdownType;
};

export const buildMonthYear = (deltaMonth = 0): Date => {
    let monthYear = new Date();
    monthYear.setDate(1);
    monthYear = addMonths(monthYear, deltaMonth);
    return monthYear;
};

export const buildSalaryForecast = (props?: Partial<SalaryForecast>) => {
    const base = {
        __typename: "SalaryForecast",
        grossSalary: 381000,
        includedFeesAmount: 23150,
        includedDeductionsAmount: 8820,
        invoiceSalary: 411000,
        monthYear: buildMonthYear().toISOString(),
        monthStartDate: buildMonthYear().toISOString(),
        monthEndDate: endOfMonth(buildMonthYear()).toISOString(),
        netSalary: 361000,
    };
    return combineProps(base, props);
};

export const buildInvitation = (props?: Partial<Invitation>) => {
    const base = {
        __typename: "Invitation",
        id: buildUuid(),
        senderName: "Monsieur One",
        clientName: "La compagnie du premier",
        clientLogoUrl: "https://picsum.photos/200",
        firstName: "Jean",
        lastName: "Dupont",
        createdAt: "2021-01-01T12:00:00Z",
        phoneNumber: "+41799999901",
        token: buildId("token"),
        text: "Bonjour, je vous invite à rejoindre ma compagnie.",
        mode: InvitationMode.Receiver,
    };
    return combineProps(base, props);
};

export const buildCct = (props?: Partial<Cct>) => {
    const base = {
        __typename: "CCT",
        id: buildUuid(),
        cctEditionId: buildId("cct-ed"),
        cctVersionId: buildId("cct-ver"),
        cctContractId: buildId("cct-contract"),
        name: buildName("My CCT"),
        hoursPerWeek: 42.5,
        active: true,
        intercomMainArticleId: "intercom-main-article-id",
        intercomHelpArticleId: "intercom-help-article-id",
    };
    return combineProps(base, props) as Cct;
};

export const buildSalaryDeduction = (props?: Partial<SalaryDeductionType>) => {
    const base = {
        title: "avs",
        quantity: 1,
        rate: "1.0",
        amount: 10.0,
    };
    return combineProps(base, props) as SalaryDeductionType;
};

export const buildSalaryDetails = (props?: Partial<SalaryDetails>) => {
    const base = {
        baseSalary: 501431,
        grossSalary: 543200,
        has13thSalary: true,
        invoiceSalary: 645647,
        lppPlan: buildLppPlan(),
        lseMargin: 0,
        mandatory13thMonth: false,
        minBaseSalary: 10000,
        minGrossSalary: 11000,
        minNetSalary: 9000,
        minInvoiceSalary: 12000,
        minVacationDays: 20,
        netSalary: 461999,

        thirteenMonthCompensation: 41769,
        publicHolidaysCompensation: 12345,
        vacationCompensation: 6590,
        requestedSalary: 9900,
        requestedSalaryType: SalaryType.Base,
        totalClientDeductions: 100,
        totalCompensation: 110,
        totalWorkerDeductions: 120,
        vacationDays: 20,
        clientMissionFeesRate: "1.3",
        clientDeductions: [
            buildSalaryDeduction({
                title: "avs",
                quantity: 1,
                rate: "5.3",
                amount: 28790,
            }),
            buildSalaryDeduction({
                title: "ac",
                quantity: 1,
                rate: "1.1",
                amount: 5975,
            }),
            buildSalaryDeduction({
                title: "pc_fam",
                quantity: 1,
                rate: "0.06",
                amount: 326,
            }),
            buildSalaryDeduction({
                title: "alloc_fam",
                quantity: 1,
                rate: "2.63",
                amount: 14286,
            }),
            buildSalaryDeduction({
                title: "laap",
                quantity: 1,
                rate: "0.1146",
                amount: 623,
            }),
            buildSalaryDeduction({
                title: "ijm",
                quantity: 1,
                rate: "1.85",
                amount: 10049,
            }),
            buildSalaryDeduction({
                title: "management_fees",
                quantity: 1,
                rate: "0.14",
                amount: 760,
            }),
            buildSalaryDeduction({
                title: "training_costs",
                quantity: 1,
                rate: "0.4",
                amount: 2173,
            }),
            buildSalaryDeduction({
                title: "lpp",
                quantity: 1,
                rate: "9.1",
                amount: 29923,
            }),
        ],
        workerDeductions: [
            buildSalaryDeduction({
                title: "avs",
                quantity: 1,
                rate: "5.3",
                amount: 28790,
            }),
            buildSalaryDeduction({
                title: "ac",
                quantity: 1,
                rate: "1.1",
                amount: 5975,
            }),
            buildSalaryDeduction({
                title: "pc_fam",
                quantity: 1,
                rate: "0.06",
                amount: 326,
            }),
            buildSalaryDeduction({
                title: "laanp",
                quantity: 1,
                rate: "0.73",
                amount: 3965,
            }),
            buildSalaryDeduction({
                title: "ijm",
                quantity: 1,
                rate: "1.85",
                amount: 10049,
            }),
            buildSalaryDeduction({
                title: "training_costs",
                quantity: 1,
                rate: "0.4",
                amount: 2173,
            }),
            buildSalaryDeduction({
                title: "lpp",
                quantity: 1,
                rate: "9.1",
                amount: 29923,
            }),
        ],
    };
    return combineProps(base, props) as SalaryDetails;
};

export const buildWorkingTimeSpecificSpecFixedDate = (
    date: string,
    fixedTimes?: ScheduleType[],
): DateScheduleType => {
    return {
        date,
        daySchedule: {
            timeMode: TimeMode.Fixed,
            fixedTimes: fixedTimes || [
                {
                    startTime: 0,
                    endTime: 100,
                    type: ScheduleTimeType.Work,
                },
            ],
        },
    };
};

export const buildPayrollBreakdown = (at?: Date, props?: Partial<PayrollBreakdown>) => {
    const baseDate = at || new Date();
    const yearMonth = startOfMonth(baseDate);
    const lastDay = endOfMonth(yearMonth);
    const startDate = startOfWeek(yearMonth, { weekStartsOn: 1 });
    const endDate = addDays(startOfWeek(lastDay, { weekStartsOn: 1 }), -1);

    const base = {
        __typename: "PayrollBreakdown",
        id: buildUuid(),
        createdAt: formatDateForGraphql(addDays(new Date(), 0)),
        updatedAt: formatDateForGraphql(addDays(new Date(), 0)),

        yearMonth: formatDateForGraphql(yearMonth),
        startDate: formatDateForGraphql(startDate),
        submittedAt: formatDateForGraphql(new Date()),
        endDate: formatDateForGraphql(endDate),
        status: PayrollBreakdownStatus.Open,
        deltaWork: 0,
        missionInfo: buildMissionInfo(),
        timesheets: [],
        absences: [],
        usedVacationMinutes: 8.4 * 60,
        usedVacationAmount: 0,
        totalWork100: 42.0 * 60,
        totalWork125: 5.5 * 60,
        totalWork150: 0,
        totalWorkedDays: "12.0",
        bonuses: [],
    };
    return combineProps(base, props) as PayrollBreakdown;
};

export const buildAbsence = (props?: Partial<Absence>) => {
    const baseDate = new Date();
    const yearMonth = startOfMonth(baseDate);
    const startDate = startOfWeek(yearMonth, { weekStartsOn: 1 });
    const base = {
        __typename: "Absence",
        id: buildUuid(),
        type: AbsenceType.Sickness,
        start: formatDateForGraphql(addDays(startDate, -1)),
        end: formatDateForGraphql(addDays(startDate, 3)),
        comment: "I'm sick, I can't work AAaaargggggggg",
    };
    return combineProps(base, props) as Absence;
};

export const buildMissionVacationStatus = (props?: Partial<MissionVacationSatus>) => {
    const base = {
        __typename: "MissionVacationSatus",
        missionInfo: buildMissionInfo(),
        contractHoursPerWeekFullTime: "42",
        contractVacationDaysFullTime: "25",
        contractVacationIndemnity: 100,
        contractVacationMinutes: 2000,
        contractBaseSalary: 3000,
        availableVacationAmount: 0,
        availableVacationMinutes: 180, // 3 hours
        previousYearAvailableVacationMinutes: 0,
        currentYearTotalAllowedVacationMinutes: 12600,
        accountingDate: "2022-02-20",
        availablePendingVacationAmount: 0,
        availablePendingVacationMinutes: 0,
    };
    return combineProps(base, props) as MissionVacationSatus;
};

export const buildMissionOvertimeStatus = (props?: Partial<MissionOvertimeStatus>) => {
    const base = {
        __typename: "MissionOvertimeStatus",
        missionInfo: buildMissionInfo(),
        overtimeMinutes: 60,
        accountingDate: "2022-02-20",
        overtimeHourlyInvoiceAmount: 3000,
        overtimePaidAtPercent: 100,
    };
    return combineProps(base, props) as MissionOvertimeStatus;
};

export const buildPayrollBreakdownSummary = (props?: Partial<PayrollBreakdownSummary>) => {
    const base = {
        __typename: "PayrollBreakdownSummary",
        rows: [
            {
                __typename: "SummaryRow",
                category: "salary",
                description: "Salaire de 1.-",
                productKey: "salary",
                quantity: 1,
                unitPrice: 100,
            },
        ],
    };
    return combineProps(base, props) as PayrollBreakdownSummary;
};

export const buildCancellationRequest = (
    props?: Partial<MissionCancellationRequest>,
): MissionCancellationRequest => {
    const base = {
        __typename: "MissionCancellationRequest",
        id: buildUuid(),
        createdAt: new Date().toISOString(),
        reason: TerminateReason.Other,
        otherReason: "I'm sick, I can't work AAaaargggggggg",
        missionInfo: buildMissionInfo(),
        requestedRole: TerminatedByType.Client,
        isCancelledBySelf: true,
    };
    return combineProps(base, props) as MissionCancellationRequest;
};

export const buildIjmGroup = (props?: Partial<IjmGroup>) => {
    const base = {
        __typename: "IjmChoice",
        id: buildUuid(),
        name: "IJM Name",
        description: "IJM Description",
        maxAnnualSalary: 100000,
        waitingPeriodDays: 30,
        benefitPeriodDays: 30,
        clientRate: "0.5",
        workerRate: "0.5",
        contractId: "GM-2025",
    };
    return combineProps(base, props) as IjmGroup;
};

export const IJM_CHOICES_MOCK = {
    request: {
        query: GET_IJM_CHOICES,
    },
    maxUsageCount: Number.POSITIVE_INFINITY,
    variableMatcher: () => true,
    result: () => ({
        data: {
            getIjmChoices: [
                {
                    __typename: "IjmChoice",
                    id: "GM-G1-730-2",
                    name: "2 jours de délai d'attente",
                    description: "2 jours de délai d'attente",
                    waitingPeriodDays: 2,
                    benefitPeriodDays: 730,
                    clientRate: "1.85",
                    workerRate: "1.85",
                },
                {
                    __typename: "IjmChoice",
                    id: "GM-G1-730-14",
                    name: "14 jours de délai d'attente",
                    description: "14 jours de délai d'attente",
                    waitingPeriodDays: 14,
                    benefitPeriodDays: 730,
                    clientRate: "0.86",
                    workerRate: "0.86",
                },
                {
                    __typename: "IjmChoice",
                    id: "GM-G1-730-30",
                    name: "30 jours de délai d'attente",
                    description: "30 jours de délai d'attente",
                    waitingPeriodDays: 30,
                    benefitPeriodDays: 730,
                    clientRate: "0.67",
                    workerRate: "0.67",
                },
            ],
        },
    }),
};

export const buildGraphqlError = (message: string): Error => {
    return new Error(message);
};

export const buildAuditLog = (id: string, mode?: boolean) => {
    return {
        __typename: "AuditLog",
        id,
        text: `a ${mode ? "allumé" : "éteint"} la lumière`,
        createdAt: "2025-01-01",
        userAvatarUrl:
            "https://img.buzzfeed.com/buzzfeed-static/static/2016-04/6/6/enhanced/webdr06/original-grid-image-20991-1459936919-1.jpg",
        userDisplayName: "Jacques Ouille",
        event: "event",
    } as AuditLog;
};

const PAGE_RESULT_SIZE = 10;

const buildPageResult = (elements: AuditLog[], currentOffset = 0) => {
    const hasNextPage = elements.length - currentOffset >= PAGE_RESULT_SIZE;
    const hasPreviousPage = currentOffset >= PAGE_RESULT_SIZE;

    const pageInfo = {
        __typename: "PageInfo",
        currentOffset,
        hasNextPage,
        hasPreviousPage,
        limit: PAGE_RESULT_SIZE,
        nextOffset: hasNextPage ? currentOffset + PAGE_RESULT_SIZE : null,
        previousOffset: hasPreviousPage ? currentOffset - PAGE_RESULT_SIZE : null,
    } as PageInfo;

    return {
        elements: elements.slice(currentOffset, currentOffset + PAGE_RESULT_SIZE),
        count: elements.length,
        pageInfo,
    };
};

export const buildAuditLogPageResult = (elementNb: number, offset = 0) => {
    const elements = Array.from({ length: elementNb }, (_, i) =>
        buildAuditLog(`id-${i}`, i % 2 === 0),
    );
    return buildPageResult(elements, offset);
};

export const buildClientUser = (props?: Partial<ClientUser>) => {
    const base = {
        __typename: "ClientUser",
        id: buildUuid(),
        email: buildEmail(),
        avatarUrl:
            "https://storage.googleapis.com/lift-public-assets/dev-storybook/client-avatar.png",
        firstName: "Kevin",
        lastName: "Kliant",
        phoneNumber: "+41799999901",
        clientInfo: buildClientInfo(),
        clientProfile: buildClientProfile(),
        workerProfile: null,
        joinedAt: "2022-02-20T00:00:00Z",
    };
    return combineProps(base, props) as ClientUser;
};

export const groupScheduleTimeFromTrackingDate = (
    trackingDates: [DateScheduleInput],
): Record<ScheduleTimeType, number> => {
    return trackingDates.reduce(
        (acc, td): Record<ScheduleTimeType, number> => {
            return (
                td.daySchedule?.fixedTimes?.reduce(
                    (_, time) => {
                        if (time.type) {
                            const current = acc[time.type] || 0;
                            const total = time.endTime - time.startTime;
                            acc[time.type] = current + total;
                        }
                        return acc;
                    },
                    {} as Record<ScheduleTimeType, number>,
                ) || ({} as Record<ScheduleTimeType, number>)
            );
        },
        {} as Record<ScheduleTimeType, number>,
    );
};

const mapScheduleTimeTypeToWorkingTimeDurationBreakdownItemType = (
    input: ScheduleTimeType,
): WorkingTimeDurationBreakdownItemType => {
    switch (input) {
        case ScheduleTimeType.Work:
            return WorkingTimeDurationBreakdownItemType.Day;
        case ScheduleTimeType.Vacation:
            return WorkingTimeDurationBreakdownItemType.Vacation;
        case ScheduleTimeType.Sickness:
            return WorkingTimeDurationBreakdownItemType.Sickness;
        case ScheduleTimeType.Trainee:
            return WorkingTimeDurationBreakdownItemType.Trainee;
        case ScheduleTimeType.Accident:
            return WorkingTimeDurationBreakdownItemType.Accident;
        case ScheduleTimeType.Maternity:
            return WorkingTimeDurationBreakdownItemType.Maternity;
        case ScheduleTimeType.Marriage:
            return WorkingTimeDurationBreakdownItemType.Marriage;
        case ScheduleTimeType.Moving:
            return WorkingTimeDurationBreakdownItemType.Moving;
        case ScheduleTimeType.PublicHoliday:
            return WorkingTimeDurationBreakdownItemType.PublicHoliday;
        case ScheduleTimeType.SicknessCloseRelative:
            return WorkingTimeDurationBreakdownItemType.SicknessCloseRelative;
        case ScheduleTimeType.DeathCloseRelative:
            return WorkingTimeDurationBreakdownItemType.DeathCloseRelative;
        default:
            throw new Error(`Unhandled ScheduleTimeType: ${input}`);
    }
};

export const buildBreakdownFromTrackingDates = (
    trackingDates: [DateScheduleInput],
):
    | {
          items: Array<{
              hourRate: number;
              type: WorkingTimeDurationBreakdownItemType;
              totalDurationMinutes: number;
          }>;
      }
    | undefined => {
    const grouped = groupScheduleTimeFromTrackingDate(trackingDates);
    if (grouped) {
        const items = Object.entries(grouped).map(([key, value]) => ({
            hourRate: 100,
            type: mapScheduleTimeTypeToWorkingTimeDurationBreakdownItemType(
                key as ScheduleTimeType,
            ),
            totalDurationMinutes: value,
        }));
        return { items };
    }
    return undefined;
};

export const buildMissionSalary = (props?: Partial<MissionSalary>) => {
    const base = {
        grossAmount: 100000,
        missionInfo: buildMissionInfo(),
    };
    return combineProps(base, props) as MissionSalary;
};

export const buildSalary = (props?: Partial<Salary>) => {
    const baseDate = new Date();
    const yearMonth = startOfMonth(baseDate);
    const base = {
        __typename: "Salary",
        id: buildUuid(),
        grossAmount: 100000,
        missionSalaries: [buildMissionSalary()],
        missionsInfo: [],
        month: yearMonth.getMonth() + 1, // JS months are 0-indexed
        netAmount: 90000,
        paidAt: formatDateForGraphql(new Date()),
        salaryAsset: buildAsset(),
        year: yearMonth.getFullYear(),
        yearMonth: formatDateForGraphql(yearMonth),
    };
    return combineProps(base, props) as Salary;
};

export const CCTS_WITH_LSE: Record<string, Cct> = {
    "cct-1": buildCct({
        cctEditionId: "cct-1",
        name: "CCT 1 (default)",
        cctContractId: "815001", // see env.CCT_DEFAULT_CONTRACT_ID
        hoursPerWeek: 44.0,
        active: true,
    }),
    "cct-2": buildCct({
        cctEditionId: "cct-2",
        name: "CCT 2 (no params)",
        cctContractId: "000001", // see env.CCT_DEFAULT_CONTRACT_ID
        hoursPerWeek: 42.0,
        active: true,
    }),
    "cct-3": buildCct({
        cctEditionId: "cct-3",
        name: "CCT 3 (complex params)",
        cctContractId: "000002", // see env.CCT_DEFAULT_CONTRACT_ID
        hoursPerWeek: 35.0,
        active: true,
    }),
};

export const CCTS_WITHOUT_LSE: Record<string, Cct> = {
    "cct-1": buildCct({
        cctEditionId: "cct-1",
        name: "CCT 1 (default)",
        cctContractId: "815001", // see env.CCT_DEFAULT_CONTRACT_ID
        hoursPerWeek: 44.0,
        active: true,
    }),
    "cct-2": buildCct({
        cctEditionId: "cct-2",
        name: "CCT 2 (no params)",
        cctContractId: "000001", // see env.CCT_DEFAULT_CONTRACT_ID
        hoursPerWeek: 42.0,
        active: true,
        intercomHelpArticleId: null,
    }),
    "cct-3": buildCct({
        cctEditionId: "cct-3",
        name: "CCT 3 (complex params)",
        cctContractId: "000002", // see env.CCT_DEFAULT_CONTRACT_ID
        hoursPerWeek: 35.0,
        active: true,
    }),
    "lift-no-cct-edition-1": buildCct({
        cctEditionId: "lift-no-cct-edition-1",
        name: "No CCT",
        cctContractId: "lift-no-cct",
        hoursPerWeek: 50.0,
        active: true,
        intercomMainArticleId: null,
        intercomHelpArticleId: null,
    }),
};

/**
 * Details fields for each CCT
 * notes:
 * - returning empty array is not permitted
 * - returning empty values for a field is not permitted
 */
export const CCTS_FIELDS: Record<string, Array<CctDetailsField>> = {
    // default cct fields
    // this contains fields that SHOULD NOT be displayed in the UI ( year + region )
    "cct-1": [
        {
            id: 1,
            name: "Année",
            uniqName: "Année",
            values: [
                {
                    criteriaListEntryId: 11,
                    criteriaStructureId: 11,
                    value: "2023",
                },
                {
                    criteriaListEntryId: 12,
                    criteriaStructureId: 12,
                    value: "2024",
                },
            ],
        },
        {
            id: 2,
            name: "KantonRegion",
            uniqName: "KantonRegion",
            values: [
                {
                    criteriaListEntryId: 21,
                    criteriaStructureId: 21,
                    value: "VD",
                },
                {
                    criteriaListEntryId: 22,
                    criteriaStructureId: 22,
                    value: "VS",
                },
            ],
        },
    ],
    // simple case
    "cct-2": [
        {
            id: 3,
            name: "param 2",
            uniqName: "Param 2",
            values: [
                {
                    criteriaListEntryId: 31,
                    criteriaStructureId: 31,
                    value: null,
                },
            ],
        },
    ],
    // Complex case
    "cct-3": [
        {
            id: 4,
            name: "First field",
            uniqName: "First field",
            values: [
                {
                    criteriaListEntryId: 41,
                    criteriaStructureId: 41,
                    value: "Field 1 - value A",
                },
                {
                    criteriaListEntryId: 42,
                    criteriaStructureId: 42,
                    value: "Field 1 - value B",
                },
            ],
        },
        {
            id: 5,
            name: "Second field",
            uniqName: "Second field",
            values: [
                {
                    criteriaListEntryId: 51,
                    criteriaStructureId: 51,
                    value: "Field 2 - value A",
                },
                {
                    criteriaListEntryId: 52,
                    criteriaStructureId: 52,
                    value: "Field 2 - value B",
                },
            ],
            children: [
                {
                    id: 100,
                    name: "Second-Alpha field",
                    uniqName: "Second-Alpha field",
                    values: [],
                },
                {
                    id: 101,
                    name: "Second-Beta field",
                    uniqName: "Second-Beta field",
                    values: [],
                },
            ],
        },
    ],
    "lift-no-cct": [],
};

export const CCTS_FIELD_VALUES: Record<
    string,
    Array<CctDetailsFieldValue & { __typename: "CCTDetailsFieldValue" }>
> = {
    "cct-3-100": [
        {
            __typename: "CCTDetailsFieldValue",
            criteriaListEntryId: 251,
            criteriaStructureId: 251,
            value: null,
            autoSelected: false,
        },
    ],
    "cct-3-101": [
        {
            __typename: "CCTDetailsFieldValue",
            criteriaListEntryId: 351,
            criteriaStructureId: 351,
            value: "Second-Beta field - value A",
            autoSelected: true,
        },
        {
            __typename: "CCTDetailsFieldValue",
            criteriaListEntryId: 352,
            criteriaStructureId: 352,
            value: "Second-Beta field - value B",
            autoSelected: false,
        },
    ],
};
